import {Injectable, NgZone} from '@angular/core';
import {App} from '@capacitor/app';
import {Dialog} from '@capacitor/dialog';
import {Location} from '@angular/common';
import {MobileService} from '../page/mobile.service';

type PluginListenerHandle = {
  remove: () => Promise<void>;
};

@Injectable({
  providedIn: 'root',
})
export class MobileBackButtonService {
  private backButtonListener: PluginListenerHandle | null = null;

  constructor(
    private location: Location,
    private mobileService: MobileService,
    private ngZone: NgZone
  ) {}

  /**
   * Initialize the hardware back button handler for Android.
   * Listening for this event will disable the default back button behaviour.
   */
  public async initializeBackButton(): Promise<void> {
    if (!this.mobileService.isMobilePlatform()) {
      return;
    }

    // Add back button listener - this disables default Android back button behavior
    // We need to manually handle navigation with window.history.back()
    this.backButtonListener = await App.addListener('backButton', event => {
      this.handleBackButton(event);
    });
  }

  /**
   * Handle the back button press.
   * Manually navigate back using Angular's Location service.
   * If canGoBack is false, ask user if they want to close the app.
   */
  private handleBackButton(event: {canGoBack: boolean}): void {
    this.ngZone.run(async () => {
      const canGoBack = event.canGoBack;

      if (canGoBack) {
        // Navigate back in the app
        this.location.back();
      } else {
        // At root - ask user if they want to close the app
        await this.showExitConfirmation();
      }
    });
  }

  /**
   * Show a confirmation dialog asking the user if they want to close the app.
   */
  private async showExitConfirmation(): Promise<void> {
    try {
      const result = await Dialog.confirm({
        title: 'Close Fullsked',
        message: 'Are you sure you want to close Fullsked?',
        okButtonTitle: 'Yes',
        cancelButtonTitle: 'No',
      });

      if (result.value) {
        // User clicked "Yes" - exit the app
        await App.exitApp();
      }
      // If user clicked "No", just stay on current page
    } catch (error) {
      console.error('Error showing exit confirmation dialog:', error);
    }
  }

  /**
   * Cleanup the back button listener.
   * Should be called in ngOnDestroy.
   */
  public destroy(): void {
    if (this.backButtonListener) {
      this.backButtonListener.remove();
      this.backButtonListener = null;
    }
  }
}
